import {ConfigComponenteKit} from '../../../../server/domain/kit/ConfigComponenteKit';
import {Component, HostListener, OnDestroy, OnInit, EventEmitter, Input, Output} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {DomSanitizer, SafeResourceUrl, SafeUrl} from '@angular/platform-browser';
import {
  AdicionalCardapioDraft,
  CatalogoImportacaoDraft,
  CategoriaCardapioDraft,
  ConfiguracaoPizzaImportacaoDraft,
  ImportadorCardapioIaService,
  PendenciaCardapioDraft,
  ProdutoCardapioDraft,
  ResultadoExtracaoCardapio,
  ResultadoImportacaoCardapioIA,
  TamanhoPizzaImportacaoDraft
} from '../../services/importador-cardapio-ia.service';
import {SocketService} from '../../services/socket.service';
import {AutorizacaoService} from '../../services/autorizacao.service';
import {EmpresasService} from '../../superadmin/services/empresas.service';
import {ConfirmacaoV2} from '../confirmacao-v2';
import {OnbFocoService} from '../../onboarding-painel/apoio/onb-foco.service';

type EtapaImportadorIA = 'enviar' | 'analisar' | 'revisar' | 'concluir';
type ModoEnvioIA = 'arquivos' | 'texto';
type FiltroRevisao = 'todos' | 'pendencias' | 'prontos';

interface ArquivoSelecionadoVM {
  file: File;
  nome: string;
  tamanho: string;
  tipo: 'pdf' | 'imagem';
  erro: string | null;
  // miniatura da foto: a object URL bruta (para revogar) e a versão confiável para o template
  previewUrl: SafeUrl | null;
  previewUrlBruta: string | null;
  // prévia do PDF: quem renderiza é o visualizador nativo do navegador, sem biblioteca extra
  previewPdfUrl: SafeResourceUrl | null;
}

interface ProdutoRevisaoVM {
  draft: ProdutoCardapioDraft;
  categoria: CategoriaRevisaoVM;
  pendencias: PendenciaCardapioDraft[];
  bloqueado: boolean;
  editandoPreco: boolean;
  precoEdicao: string;
}

interface CategoriaRevisaoVM {
  draft: CategoriaCardapioDraft;
  indice: number;
  produtos: ProdutoRevisaoVM[];
  totalPendencias: number;
  totalBloqueantes: number;
}

interface EtapaAnaliseVM {
  id: string;
  rotulo: string;
  estado: 'concluida' | 'ativa' | 'futura';
}

const MAX_BYTES_PDF = 10 * 1024 * 1024;
const MAX_BYTES_IMAGEM = 5 * 1024 * 1024;
const MAX_IMAGENS = 10;
// mesmo limite do servidor para o texto colado
const MAX_BYTES_TEXTO = 200 * 1024;
const EXTENSOES_IMAGEM = ['jpg', 'jpeg', 'png', 'webp'];
// print da área de transferência não tem nome de arquivo: este é o rótulo que aparece na lista
const NOME_BASE_COLADO = 'Imagem colada';

@Component({
  selector: 'app-tela-importador-cardapio-ia',
  templateUrl: './tela-importador-cardapio-ia.component.html',
  styleUrls: ['./tela-importador-cardapio-ia.component.scss']
})
export class TelaImportadorCardapioIaComponent implements OnInit, OnDestroy {
  etapa: EtapaImportadorIA = 'enviar';
  etapas: Array<{id: EtapaImportadorIA, rotulo: string, descricao: string}> = [
    {id: 'enviar', rotulo: 'Enviar', descricao: 'PDF, fotos ou texto do cardápio'},
    {id: 'analisar', rotulo: 'Analisar', descricao: 'A IA lê produtos e preços'},
    {id: 'revisar', rotulo: 'Revisar', descricao: 'Você ajusta o que quiser'},
    {id: 'concluir', rotulo: 'Concluir', descricao: 'Só o aprovado entra'}
  ];

  // etapa 1
  arquivos: ArquivoSelecionadoVM[] = [];
  // arquivos (PDF/fotos) ou texto colado: o modo escolhido é a única fonte enviada
  modoEnvio: ModoEnvioIA = 'arquivos';
  textoCardapio = '';
  arrastando = false;
  enviando = false;
  erroEnvio: string = null;
  private contadorArrasto = 0;

  // ⌘V/Ctrl+V na etapa de envio: o print ou o PDF da área de transferência entra
  // como arquivo sem precisar salvar antes. O pulso existe porque a lista de
  // arquivos fica abaixo da dropzone — sem ele o colar parece não ter acontecido.
  colando = false;
  // colou texto no modo de arquivos: em vez do beco sem saída, oferece o caminho certo em um clique
  textoColadoPendente: string = null;
  readonly ehMac = /Mac|iPhone|iPad|iPod/i.test(
    (typeof navigator !== 'undefined' && (navigator.platform || navigator.userAgent)) || '');
  private pulsoColarTimer: any = null;

  // etapa 2
  erroAnalise: string = null;
  progressoAnalise: {produtos: number, categorias: number, tokensSaida: number} = null;
  private progressoSub: any = null;
  etapasAnalise: EtapaAnaliseVM[] = [];
  private timersAnalise: any[] = [];

  // frases rotativas de chef enquanto a IA "cozinha" (neutras de segmento)
  private static FRASES_CHEF = [
    'Colocando o avental…',
    'Acendendo o fogão…',
    'Lendo o cardápio com carinho…',
    'Anotando os preços no caderninho…',
    'Separando os pratos por categoria…',
    'Conferindo os adicionais um a um…',
    'Provando o molho…',
    'Escolhendo os melhores ingredientes…',
    'Organizando a cozinha…',
    'Caprichando na apresentação…',
    'Passando tudo a limpo…',
    'Montando o cardápio na bandeja…'
  ];
  fraseChef = '';
  alternadorFrase = false;
  private fraseTimer: any = null;
  private indiceFrase = 0;

  // barra de progresso guiada pelos tokens reais que o servidor manda via socket:
  // curva saturante tokens/(tokens + K) — honesta sem conhecer o tamanho final do
  // rascunho e nunca fecha antes da resposta real. O timer só dá movimento suave
  // entre eventos do socket, correndo no máximo 10 pontos à frente dos tokens
  progressoBarra = 0;
  private barraTimer: any = null;
  private alvoTokensBarra = 0;
  private etapaAnaliseAtiva = 0;
  private static DURACAO_ESTIMADA_ANALISE_MS = 3 * 60 * 1000;
  private static INTERVALO_BARRA_MS = 400;
  private static TOKENS_MEIA_BARRA = 6000;

  // custo da análise: visível somente para admins do MeuCardápio
  ehAdminMeuCardapio = false;
  analiseRealizada: ResultadoExtracaoCardapio = null;

  // etapa 3
  rascunho: CatalogoImportacaoDraft = null;
  categorias: CategoriaRevisaoVM[] = [];
  filtro: FiltroRevisao = 'todos';
  busca = '';
  produtoAberto: ProdutoRevisaoVM = null;
  grupoAberto: AdicionalCardapioDraft = null;
  importando = false;
  erroImportacao: string = null;
  confirmandoImportacao = false;
  readonly confirmacao = new ConfirmacaoV2();
  private avisosOriginais: PendenciaCardapioDraft[] = [];
  private revalidacaoTimer: any = null;
  pendenciasGerais: PendenciaCardapioDraft[] = [];
  avisosLeituraAbertos = false;
  templatePizzaExistente: any = null;
  carregandoTemplatePizza = false;
  templatesPizzaCarregados = false;
  erroTemplatePizza: string = null;

  // etapa 4
  resultadoImportacao: ResultadoImportacaoCardapioIA = null;
  ignorados: string[] = [];
  reimportando = false;
  private referenciasJaImportadas: {[referencia: string]: boolean} = {};
  private voltarParaUrl = '/admin/produtos-v2';

  // embutido no wizard de nova empresa (superadmin): sem cabeçalho, importa na empresa alvo
  // e, ao terminar, avisa o pai em vez de navegar
  @Input() embutido = false;
  @Input() empresaAlvo: {id: number; nome?: string} = null;
  @Output() onConcluido = new EventEmitter<ResultadoImportacaoCardapioIA>();

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private servico: ImportadorCardapioIaService,
              private socketService: SocketService,
              private autorizacaoService: AutorizacaoService,
              private empresasService: EmpresasService,
              private sanitizer: DomSanitizer,
              private foco: OnbFocoService) { }

  ngOnInit() {
    const usuario: any = this.autorizacaoService.getUser() || {};

    this.ehAdminMeuCardapio = !!(usuario.admin || usuario.superadmin);
    this.voltarParaUrl = this.normalizeUrlVoltar(this.activatedRoute.snapshot.queryParamMap.get('voltarPara'));
  }

  ngOnDestroy() {
    if (this.revalidacaoTimer) clearTimeout(this.revalidacaoTimer);
    if (this.pulsoColarTimer) clearTimeout(this.pulsoColarTimer);

    this.limpeProgressoAnalise();
    this.revoguePreviews(this.arquivos);
  }

  // ── navegação geral ───────────────────────────────────────────

  @HostListener('window:beforeunload', ['$event'])
  protejaContraRecarga(evento: BeforeUnloadEvent) {
    if (this.haTrabalhoNaoSalvo) {
      evento.preventDefault();
      evento.returnValue = true;
    }
  }

  @HostListener('document:keydown.escape')
  aoPressionarEscape() {
    if (this.confirmandoImportacao) this.fecheConfirmacaoImportar();
    else if (this.produtoAberto) this.fecheProduto();
  }

  private get haTrabalhoNaoSalvo(): boolean {
    return (this.etapa === 'revisar' && !!this.rascunho) || (this.etapa === 'analisar' && this.enviando);
  }

  async volteParaProdutos() {
    if (this.etapa === 'revisar' && this.rascunho) {
      const sair = await this.confirmacao.confirme(
        'Sair da revisão?',
        'O rascunho deste cardápio será perdido e a análise terá de ser refeita.',
        'Sair sem importar'
      );
      if (!sair) return;
    }

    if (this.embutido) {
      this.onConcluido.emit(this.resultadoImportacao);
      return;
    }

    this.router.navigateByUrl(this.voltarParaUrl);
  }

  private get idEmpresaAlvo(): number {
    return this.empresaAlvo ? Number(this.empresaAlvo.id) : null;
  }

  private normalizeUrlVoltar(url: string): string {
    if (url && url.charAt(0) === '/' && url.indexOf('//') !== 0 && url.indexOf('://') === -1) return url;

    return '/admin/produtos-v2';
  }

  indiceEtapa(id: EtapaImportadorIA): number {
    return this.etapas.findIndex((etapa) => etapa.id === id);
  }

  estadoEtapa(id: EtapaImportadorIA): 'concluida' | 'ativa' | 'futura' {
    const atual = this.indiceEtapa(this.etapa);
    const alvo = this.indiceEtapa(id);

    if (alvo < atual) return 'concluida';
    if (alvo === atual) return 'ativa';
    return 'futura';
  }

  // ── etapa 1: enviar ───────────────────────────────────────────

  abraSeletorDeArquivo(input: HTMLInputElement) {
    input.click();
  }

  aoEntrarArrasto(evento: DragEvent) {
    evento.preventDefault();
    this.contadorArrasto++;
    this.arrastando = true;
  }

  aoArrastarSobre(evento: DragEvent) {
    evento.preventDefault();
  }

  aoSairArrasto(evento: DragEvent) {
    evento.preventDefault();
    // dragleave dispara ao passar sobre os filhos da dropzone; o contador evita o pisca-pisca
    this.contadorArrasto = Math.max(0, this.contadorArrasto - 1);

    if (this.contadorArrasto === 0) this.arrastando = false;
  }

  aoSoltarArquivos(evento: DragEvent) {
    evento.preventDefault();
    this.contadorArrasto = 0;
    this.arrastando = false;

    if (evento.dataTransfer && evento.dataTransfer.files)
      this.adicioneArquivos(this.arrayDeArquivos(evento.dataTransfer.files));
  }

  aoEscolherArquivos(evento: any) {
    if (evento.target && evento.target.files)
      this.adicioneArquivos(this.arrayDeArquivos(evento.target.files));

    evento.target.value = '';
  }

  // ⌘V/Ctrl+V em qualquer ponto da etapa de envio: quem acabou de dar um print do
  // cardápio cola direto, sem salvar o arquivo antes. Escuta no documento porque
  // exigir foco na dropzone quebraria a expectativa de "copiei, colei".
  @HostListener('document:paste', ['$event'])
  aoColar(evento: ClipboardEvent) {
    if (this.etapa !== 'enviar' || this.enviando || !evento.clipboardData) return;

    const arquivos = this.arquivosDaAreaDeTransferencia(evento.clipboardData);

    if (arquivos.length) {
      evento.preventDefault();
      // colou uma foto com a aba de texto aberta: o destino é óbvio, a aba acompanha
      this.modoEnvio = 'arquivos';
      this.adicioneArquivos(arquivos);
      this.pulseAColagem();
      return;
    }

    // texto colado dentro de um campo é assunto do navegador — só o que cai na tela é nosso
    if (this.emCampoDeTexto(evento.target)) return;

    const texto = (evento.clipboardData.getData('text/plain') || '').trim();
    if (!texto) return;

    evento.preventDefault();

    // aba de texto aberta e campo vazio sem foco: colar não fazia nada, agora cai direto no campo
    if (this.modoEnvio === 'texto') {
      if (!this.textoPreenchido) this.textoCardapio = texto;
      return;
    }

    this.textoColadoPendente = texto;
  }

  // o que o colar trouxe: print da tela (sem nome próprio) ou arquivo copiado do Finder/Explorer
  private arquivosDaAreaDeTransferencia(dados: DataTransfer): File[] {
    const brutos: File[] = dados.files && dados.files.length
      ? this.arrayDeArquivos(dados.files)
      // Safari antigo não preenche `files` no colar; os itens continuam lá
      : Array.prototype.slice.call(dados.items || [])
        .filter((item: DataTransferItem) => item.kind === 'file')
        .map((item: DataTransferItem) => item.getAsFile())
        .filter((file: File) => !!file);

    let colados = this.arquivos.filter((arquivo) => arquivo.nome.indexOf(NOME_BASE_COLADO) === 0).length;

    return brutos.map((file) => {
      if (!this.ehNomeGenerico(file.name)) return file;

      // print da área de transferência chega como `image.png` ou sem nome nenhum:
      // rotular aqui é o que deixa a lista legível quando há mais de um
      colados++;
      const nome = String(`${NOME_BASE_COLADO}${colados > 1 ? ` ${colados}` : ''}.${this.extensaoDoTipo(file.type)}`);

      return new File([file], nome, {type: file.type});
    });
  }

  private ehNomeGenerico(nome: string): boolean {
    const base = (nome || '').split('.')[0].toLowerCase();

    return !base || base === 'image' || base === 'imagem' || base === 'blob';
  }

  private extensaoDoTipo(tipo: string): string {
    if (tipo === 'image/jpeg' || tipo === 'image/jpg') return 'jpg';
    if (tipo === 'application/pdf') return 'pdf';

    // png, webp e qualquer outro: o subtipo é a extensão, e o que não for aceito cai na validação
    return ((tipo || '').split('/')[1] || 'bin').toLowerCase();
  }

  private emCampoDeTexto(alvo: any): boolean {
    if (!alvo || !alvo.tagName) return false;
    const tag = String(alvo.tagName).toLowerCase();

    return tag === 'textarea' || tag === 'input' || alvo.isContentEditable === true;
  }

  private pulseAColagem() {
    if (this.pulsoColarTimer) clearTimeout(this.pulsoColarTimer);
    this.colando = true;
    this.pulsoColarTimer = setTimeout(() => this.colando = false, 700);
  }

  // o texto colado por engano no modo de arquivos vira um clique, não um recomeço
  useTextoColado() {
    this.modoEnvio = 'texto';
    this.textoCardapio = this.textoColadoPendente;
    this.textoColadoPendente = null;
    this.erroEnvio = null;
  }

  descarteTextoColado() {
    this.textoColadoPendente = null;
  }

  private arrayDeArquivos(lista: FileList): File[] {
    const novos: File[] = [];
    for (let i = 0; i < lista.length; i++) novos.push(lista.item(i));

    return novos;
  }

  // um PDF sozinho ou várias fotos: escolher o outro tipo substitui a seleção atual
  private adicioneArquivos(novos: File[]) {
    this.erroEnvio = null;
    // chegou arquivo: a dica de "isso era texto" perdeu o sentido
    this.textoColadoPendente = null;

    const pdf = novos.find((file) => this.extensaoDe(file) === 'pdf');

    if (pdf) {
      this.revoguePreviews(this.arquivos);

      const erroPdf = pdf.size > MAX_BYTES_PDF ? 'O PDF deve ter no máximo 10 MB.' : null;
      // blob: não está na lista de esquemas seguros do Angular — precisa do bypass
      const previewUrlBruta = erroPdf ? null : URL.createObjectURL(pdf);

      this.arquivos = [{
        file: pdf,
        nome: pdf.name,
        tamanho: this.formateTamanho(pdf.size),
        tipo: 'pdf',
        erro: erroPdf,
        previewUrl: null,
        previewUrlBruta,
        // os parâmetros de exibição são dica: o Chrome respeita, Firefox e Safari ignoram sem quebrar
        previewPdfUrl: previewUrlBruta
          ? this.sanitizer.bypassSecurityTrustResourceUrl(String(`${previewUrlBruta}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`))
          : null
      }];
      return;
    }

    // trocou o PDF por fotos: a prévia do PDF também segura uma object URL, precisa revogar
    if (this.arquivos.some((arquivo) => arquivo.tipo === 'pdf')) {
      this.revoguePreviews(this.arquivos);
      this.arquivos = [];
    }

    for (const file of novos) {
      const extensao = this.extensaoDe(file);
      let erro: string = null;

      if (EXTENSOES_IMAGEM.indexOf(extensao) < 0)
        erro = 'Envie o cardápio em PDF ou fotos JPG, PNG ou WebP.';
      else if (file.size > MAX_BYTES_IMAGEM)
        erro = 'Cada foto deve ter no máximo 5 MB.';

      // blob: não está na lista de esquemas seguros do Angular — precisa do bypass
      const previewUrlBruta = erro ? null : URL.createObjectURL(file);

      this.arquivos.push({
        file,
        nome: file.name,
        tamanho: this.formateTamanho(file.size),
        tipo: 'imagem',
        erro,
        previewUrl: previewUrlBruta ? this.sanitizer.bypassSecurityTrustUrl(previewUrlBruta) : null,
        previewUrlBruta,
        previewPdfUrl: null
      });
    }

    if (this.arquivos.length > MAX_IMAGENS) {
      this.revoguePreviews(this.arquivos.slice(MAX_IMAGENS));
      this.arquivos = this.arquivos.slice(0, MAX_IMAGENS);
      this.erroEnvio = String(`No máximo ${MAX_IMAGENS} fotos por análise — mantivemos as ${MAX_IMAGENS} primeiras.`);
    }
  }

  // object URLs de preview precisam ser revogadas, senão as fotos ficam presas na memória
  private revoguePreviews(arquivos: ArquivoSelecionadoVM[]) {
    for (const arquivo of arquivos)
      if (arquivo.previewUrlBruta) URL.revokeObjectURL(arquivo.previewUrlBruta);
  }

  private extensaoDe(file: File): string {
    return (file.name.split('.').pop() || '').toLowerCase();
  }

  // a miniatura é pequena demais para ler o cardápio: o clique abre o arquivo inteiro em outra aba
  abraArquivo(arquivo: ArquivoSelecionadoVM) {
    if (arquivo.previewUrlBruta) window.open(arquivo.previewUrlBruta, '_blank');
  }

  removaArquivo(indice: number) {
    this.revoguePreviews(this.arquivos.slice(indice, indice + 1));
    this.arquivos.splice(indice, 1);
    this.erroEnvio = null;
  }

  private formateTamanho(bytes: number): string {
    if (bytes >= 1024 * 1024) return String(`${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`);
    return String(`${Math.max(1, Math.round(bytes / 1024))} KB`);
  }

  mudeModoEnvio(modo: ModoEnvioIA) {
    if (this.modoEnvio === modo) return;
    this.modoEnvio = modo;
    this.erroEnvio = null;
    this.textoColadoPendente = null;
  }

  // ⌘ no Mac, Ctrl no resto: mostrar a tecla errada é pior do que não mostrar nenhuma
  get teclaColar(): string {
    return this.ehMac ? '⌘' : 'Ctrl';
  }

  // tamanho do texto em bytes UTF-8, que é o que o servidor limita (acentos ocupam 2 bytes)
  get bytesTexto(): number {
    return new Blob([this.textoCardapio || '']).size;
  }

  get tamanhoTexto(): string {
    return this.bytesTexto ? this.formateTamanho(this.bytesTexto) : '0 KB';
  }

  get textoExcedeLimite(): boolean {
    return this.bytesTexto > MAX_BYTES_TEXTO;
  }

  get textoPreenchido(): boolean {
    return !!(this.textoCardapio || '').trim();
  }

  get podeAnalisar(): boolean {
    if (this.enviando) return false;
    if (this.modoEnvio === 'texto') return this.textoPreenchido && !this.textoExcedeLimite;

    return this.arquivos.length > 0 && this.arquivos.every((arquivo) => !arquivo.erro);
  }

  // na etapa de análise, o cabeçalho mostra o que foi enviado; só o PDF tem tamanho ao lado
  get tamanhoSelecao(): string {
    if (this.modoEnvio === 'texto') return this.tamanhoTexto;
    if (this.arquivos.length > 0 && this.arquivos[0].tipo === 'pdf') return this.arquivos[0].tamanho;
    return null;
  }

  get resumoSelecao(): string {
    if (this.modoEnvio === 'texto') return this.textoPreenchido ? 'Texto colado do cardápio' : null;
    if (this.arquivos.length === 0) return null;
    if (this.arquivos[0].tipo === 'pdf') return this.arquivos[0].nome;

    return String(`${this.arquivos.length} ${this.arquivos.length === 1 ? 'foto' : 'fotos'} do cardápio`);
  }

  /** Botão sempre ativo: se faltar algo, o guia de foco aponta onde tocar. */
  analiseCardapio() {
    if (this.enviando) return;
    if (!this.podeAnalisar) {
      if (this.modoEnvio === 'texto') {
        return this.foco.mostre('ia-texto-cardapio', this.textoExcedeLimite
          ? 'O texto passou do limite: apague uma parte ou mande em duas vezes.' : 'Cole aqui o texto do cardápio.');
      }
      return this.foco.mostre('ia-dropzone', this.arquivos.length
        ? 'Tire o arquivo com problema (marcado em vermelho) para continuar.' : 'Toque aqui para escolher a foto ou o PDF do cardápio.');
    }

    this.enviando = true;
    this.erroEnvio = null;
    this.erroAnalise = null;
    this.etapa = 'analisar';
    this.inicieProgressoAnalise();

    const analise = this.modoEnvio === 'texto'
      ? this.servico.analiseTexto(this.textoCardapio.trim(), this.idEmpresaAlvo)
      : this.servico.analise(this.arquivos.map((arquivo) => arquivo.file), this.idEmpresaAlvo);

    analise
      .then((resultado) => {
        this.enviando = false;
        this.limpeProgressoAnalise();
        this.analiseRealizada = resultado;
        this.apliqueRascunho(resultado.rascunho);
        this.etapa = 'revisar';
      })
      .catch((erro) => {
        this.enviando = false;
        this.limpeProgressoAnalise();
        this.erroAnalise = this.mensagemAmigavelDeAnalise(erro);
      });
  }

  tenteAnalisarNovamente() {
    this.erroAnalise = null;
    this.analiseCardapio();
  }

  // volta para a etapa de envio mantendo o modo e o texto: quem colou pode só corrigir
  envieOutroArquivo() {
    this.revoguePreviews(this.arquivos);
    this.arquivos = [];
    this.erroEnvio = null;
    this.erroAnalise = null;
    this.textoColadoPendente = null;
    this.etapa = 'enviar';
  }

  recomeceEnvio() {
    this.revoguePreviews(this.arquivos);
    this.arquivos = [];
    this.textoCardapio = '';
    this.rascunho = null;
    this.analiseRealizada = null;
    this.categorias = [];
    this.produtoAberto = null;
    this.erroAnalise = null;
    this.erroImportacao = null;
    this.resultadoImportacao = null;
    this.referenciasJaImportadas = {};
    this.templatePizzaExistente = null;
    this.templatesPizzaCarregados = false;
    this.erroTemplatePizza = null;
    this.limpeProgressoAnalise();
    this.etapa = 'enviar';
  }

  // custo da chamada de IA — só admins do MeuCardápio veem (o lojista não)
  get resumoCustoAnalise(): string {
    if (!this.ehAdminMeuCardapio || !this.analiseRealizada) return null;

    const uso: any = this.analiseRealizada.uso || {};
    const custo = uso.custoDolar != null
      ? String(`US$ ${uso.custoDolar.toFixed(4).replace('.', ',')}`)
      : 'custo indisponível';

    return String(`${custo} · ${this.formateTokens(uso.tokensEntrada)} entrada + ` +
      `${this.formateTokens(uso.tokensSaida)} saída · ${this.analiseRealizada.modelo}`);
  }

  private formateTokens(tokens: number): string {
    const total = Number(tokens) || 0;

    if (total >= 1000) return String(`${(total / 1000).toFixed(1).replace('.', ',')}k tokens`);

    return String(`${total} tokens`);
  }

  private mensagemAmigavelDeAnalise(erro: any): string {
    const mensagem = typeof erro === 'string' ? erro : (erro && erro.message);

    // mensagens internas (fornecedor, credenciais, formato) não chegam ao lojista
    if (mensagem && !/anthropic|api[_ ]?key|json|status|token/i.test(mensagem)) return mensagem;

    return 'Não conseguimos ler este cardápio. Em PDF, confira se o texto é selecionável; ' +
      'em fotos, use imagens nítidas e tente novamente.';
  }

  private inicieProgressoAnalise() {
    this.limpeProgressoAnalise();
    this.etapasAnalise = [
      {id: 'PREPARANDO', rotulo: this.modoEnvio === 'texto' ? 'Preparando o texto' : 'Preparando o arquivo', estado: 'ativa'},
      {id: 'LENDO', rotulo: 'Lendo produtos e preços', estado: 'futura'},
      {id: 'ORGANIZANDO', rotulo: 'Organizando categorias e adicionais', estado: 'futura'},
      {id: 'VALIDANDO', rotulo: 'Validando o resultado', estado: 'futura'}
    ];

    // só a primeira etapa avança por tempo (upload + preparação);
    // as demais acompanham a barra de tokens reais
    this.timersAnalise = [
      setTimeout(() => this.avanceProgressoAnalise(1), 3000)
    ];

    // progresso real via socket: o servidor conta produtos e tokens conforme a leitura avança
    this.progressoAnalise = null;
    this.progressoSub = this.socketService.on('cardapioia:progresso')
      .subscribe((progresso: any) => this.aoProgressoDaAnalise(progresso));

    this.indiceFrase = Math.floor(Math.random() * TelaImportadorCardapioIaComponent.FRASES_CHEF.length);
    this.troqueFraseChef();
    this.fraseTimer = setInterval(() => this.troqueFraseChef(), 4200);

    this.progressoBarra = 0;
    this.alvoTokensBarra = 0;
    this.etapaAnaliseAtiva = 0;
    this.barraTimer = setInterval(() => this.avanceBarra(),
      TelaImportadorCardapioIaComponent.INTERVALO_BARRA_MS);
  }

  get percentualBarra(): number {
    return Math.round(this.progressoBarra);
  }

  private aoProgressoDaAnalise(progresso: any) {
    this.progressoAnalise = progresso;

    const tokens = Number(progresso && progresso.tokensSaida) || 0;

    this.alvoTokensBarra = 95 * tokens /
      (tokens + TelaImportadorCardapioIaComponent.TOKENS_MEIA_BARRA);
    this.progressoBarra = Math.max(this.progressoBarra, this.alvoTokensBarra);

    // o checklist acompanha a barra em vez de um cronômetro
    if (this.progressoBarra >= 80) this.avanceProgressoAnalise(3);
    else if (this.progressoBarra >= 45) this.avanceProgressoAnalise(2);
    else this.avanceProgressoAnalise(1);
  }

  private avanceBarra() {
    // sem nenhum dado do socket ainda, o movimento por tempo para em 10%
    const teto = this.alvoTokensBarra > 0
      ? Math.min(95, this.alvoTokensBarra + 10)
      : 10;

    if (this.progressoBarra >= teto) return;

    const incremento = 95 * TelaImportadorCardapioIaComponent.INTERVALO_BARRA_MS /
      TelaImportadorCardapioIaComponent.DURACAO_ESTIMADA_ANALISE_MS;

    this.progressoBarra = Math.min(teto, this.progressoBarra + incremento);
  }

  private troqueFraseChef() {
    const frases = TelaImportadorCardapioIaComponent.FRASES_CHEF;

    this.fraseChef = frases[this.indiceFrase % frases.length];
    this.indiceFrase++;
    this.alternadorFrase = !this.alternadorFrase;
  }

  private avanceProgressoAnalise(indice: number) {
    if (indice <= this.etapaAnaliseAtiva) return;

    this.etapaAnaliseAtiva = indice;
    this.etapasAnalise.forEach((item, posicao) => {
      item.estado = posicao < indice ? 'concluida' : (posicao === indice ? 'ativa' : 'futura');
    });
  }

  private limpeProgressoAnalise() {
    this.timersAnalise.forEach((timer) => clearTimeout(timer));
    this.timersAnalise = [];

    if (this.barraTimer) {
      clearInterval(this.barraTimer);
      this.barraTimer = null;
    }

    if (this.fraseTimer) {
      clearInterval(this.fraseTimer);
      this.fraseTimer = null;
    }

    if (this.progressoSub) {
      this.progressoSub.unsubscribe();
      this.progressoSub = null;
    }
  }

  // ── etapa 3: revisão ──────────────────────────────────────────

  private apliqueRascunho(rascunho: CatalogoImportacaoDraft) {
    this.rascunho = rascunho;
    (rascunho.categorias || []).forEach((categoria) => (categoria.produtos || []).forEach((produto) => {
      produto.tipoProduto = ['PIZZA', 'KIT'].includes(produto.tipoProduto) ? produto.tipoProduto : 'NORMAL';
      if (produto.tipoProduto === 'KIT' && !produto.adicionais?.some(a => a.finalidade === 'COMPONENTE_KIT')) produto.configKit = {modalidadeQuantidade: 'FECHADA', qtdeMinimaPorSabor: 1, ...produto.configKit};
      (produto.adicionais || []).forEach((adicional) => {
        adicional.finalidade = ['TAMANHO_PIZZA', 'QUANTIDADE_KIT', 'SABORES_KIT', 'COMPONENTE_KIT'].includes(adicional.finalidade) ? adicional.finalidade : null;
      });
    }));

    // avisos apontados pela IA não bloqueiam: a revisão humana é o tratamento deles
    this.avisosOriginais = (rascunho.pendencias || [])
      .filter((pendencia) => !pendencia.bloqueiaImportacao || pendencia.codigo === 'AMBIGUIDADE_FONTE')
      .map((pendencia) => Object.assign({}, pendencia, {bloqueiaImportacao: false}));

    this.sincronizeConfiguracaoPizza();
    this.revalideLocalmente();
    this.carregueTemplatePizza();
  }

  get temPizzas(): boolean {
    return this.produtosPizza.length > 0;
  }

  private get produtosPizza(): ProdutoCardapioDraft[] {
    if (!this.rascunho) return [];

    return (this.rascunho.categorias || []).reduce((produtos, categoria) =>
      produtos.concat((categoria.produtos || []).filter((produto) => produto.tipoProduto === 'PIZZA')),
    [] as ProdutoCardapioDraft[]);
  }

  private sincronizeConfiguracaoPizza() {
    if (!this.rascunho) return;

    const produtosPizza = this.produtosPizza;

    if (!produtosPizza.length) {
      this.rascunho.configuracaoPizza = null;
      return;
    }

    const anterior = this.rascunho.configuracaoPizza;
    const configuracao: ConfiguracaoPizzaImportacaoDraft = {
      templateId: this.templatePizzaExistente ? Number(this.templatePizzaExistente.id) :
        (anterior && Number(anterior.templateId) > 0 ? Number(anterior.templateId) : null),
      tipoCobranca: this.templatePizzaExistente
        ? (String(this.templatePizzaExistente.tipoDeCobranca).toLowerCase() === 'media' ? 'MEDIA' : 'MAIOR')
        : (anterior && anterior.tipoCobranca === 'MAIOR' ? 'MAIOR' : 'MEDIA'),
      tamanhos: []
    };

    produtosPizza.forEach((produto) => {
      const grupo = (produto.adicionais || []).find((adicional) => adicional.finalidade === 'TAMANHO_PIZZA');

      if (!grupo) return;

      (grupo.opcoes || []).forEach((opcao) => {
        const nome = opcao.nome ? opcao.nome.trim() : '';

        if (!nome || configuracao.tamanhos.some((tamanho) => this.normalizeNome(tamanho.nome) === this.normalizeNome(nome)))
          return;

        const tamanhoAnterior = anterior && (anterior.tamanhos || []).find((tamanho) =>
          this.normalizeNome(tamanho.nome) === this.normalizeNome(nome));
        const tamanhoTemplate = this.encontreTamanhoTemplate(nome);

        configuracao.tamanhos.push({
          nome,
          qtdePedacos: tamanhoTemplate ? this.inteiroComPadrao(tamanhoTemplate.qtdePedacos, 0, 0) :
            this.inteiroComPadrao(tamanhoAnterior && tamanhoAnterior.qtdePedacos, 0, 0),
          qtdeSabores: tamanhoTemplate ? this.inteiroComPadrao(tamanhoTemplate.qtdeSabores, 1, 1) :
            this.inteiroComPadrao(tamanhoAnterior && tamanhoAnterior.qtdeSabores, 1, 1)
        });
      });
    });

    this.rascunho.configuracaoPizza = configuracao;
  }

  private carregueTemplatePizza() {
    if (!this.temPizzas || this.carregandoTemplatePizza || this.templatesPizzaCarregados) return;

    // embutido no superadmin a "empresa logada" é a Fibo, não a loja alvo (recém-criada, sem template):
    // não consulta e deixa o importador criar o template na importação
    if (this.embutido) {
      this.templatesPizzaCarregados = true;
      this.templatePizzaExistente = null;
      this.sincronizeConfiguracaoPizza();
      this.revalideLocalmente();
      return;
    }

    this.carregandoTemplatePizza = true;
    this.erroTemplatePizza = null;

    this.empresasService.obtenhaTemplatesEmpresaLogada()
      .then((templates: any[]) => {
        this.carregandoTemplatePizza = false;
        this.templatesPizzaCarregados = true;
        this.templatePizzaExistente = (Array.isArray(templates) ? templates : [])
          .find((template) => template && template.tipo === 'pizza') || null;
        this.sincronizeConfiguracaoPizza();
        this.revalideLocalmente();
      })
      .catch(() => {
        this.carregandoTemplatePizza = false;
        this.templatesPizzaCarregados = true;
        this.erroTemplatePizza = 'Não foi possível consultar o template atual. Se já existir um, ele será reutilizado na importação.';
      });
  }

  get nomeTemplatePizza(): string {
    if (this.carregandoTemplatePizza) return 'Consultando o catálogo…';
    if (this.erroTemplatePizza) return 'Será definido na importação';
    if (!this.templatePizzaExistente) return 'Um template “Pizzas” será criado';

    return String(`${this.templatePizzaExistente.nome || 'Pizzas'} (${this.templatePizzaExistente.identificador || 'Pizza'})`);
  }

  get tamanhosExistentesTemplatePizza(): string {
    if (!this.templatePizzaExistente || !Array.isArray(this.templatePizzaExistente.tamanhos)) return '';

    return this.templatePizzaExistente.tamanhos.map((tamanho) => tamanho.descricao).filter((nome) => !!nome).join(', ');
  }

  get tamanhosNovosTemplatePizza(): string[] {
    if (!this.templatePizzaExistente || !this.rascunho || !this.rascunho.configuracaoPizza) return [];

    return this.rascunho.configuracaoPizza.tamanhos
      .filter((tamanho) => !this.encontreTamanhoTemplate(tamanho.nome))
      .map((tamanho) => tamanho.nome);
  }

  tamanhoExisteNoTemplate(tamanho: TamanhoPizzaImportacaoDraft): boolean {
    return !!this.encontreTamanhoTemplate(tamanho && tamanho.nome);
  }

  private encontreTamanhoTemplate(nome: string): any {
    if (!nome || !this.templatePizzaExistente || !Array.isArray(this.templatePizzaExistente.tamanhos)) return null;

    return this.templatePizzaExistente.tamanhos.find((tamanho) =>
      this.normalizeNome(tamanho.descricao) === this.normalizeNome(nome)) || null;
  }

  private inteiroComPadrao(valor: any, padrao: number, minimo: number): number {
    const numero = Number(valor);
    return Number.isInteger(numero) && numero >= minimo ? numero : padrao;
  }

  private normalizeNome(valor: any): string {
    return String(valor || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  }

  private revalideLocalmente() {
    if (!this.rascunho) return;

    this.sincronizeConfiguracaoPizza();

    const pendencias: PendenciaCardapioDraft[] = [];

    (this.rascunho.categorias || []).forEach((categoria, indiceCategoria) => {
      const caminhoCategoria = String(`categorias[${indiceCategoria}]`);

      if (!categoria.nome || !categoria.nome.trim())
        pendencias.push({codigo: 'NOME_CATEGORIA_AUSENTE', caminho: caminhoCategoria + '.nome',
          mensagem: 'A categoria está sem nome.', bloqueiaImportacao: true});

      (categoria.produtos || []).forEach((produto, indiceProduto) => {
        const caminhoProduto = String(`${caminhoCategoria}.produtos[${indiceProduto}]`);
        const ehPizza = produto.tipoProduto === 'PIZZA';
        const grupoTamanhoPizza = ehPizza ? (produto.adicionais || [])
          .find((adicional) => adicional.finalidade === 'TAMANHO_PIZZA') : null;

        if (!produto.nome || !produto.nome.trim())
          pendencias.push({codigo: 'NOME_PRODUTO_AUSENTE', caminho: caminhoProduto + '.nome',
            mensagem: 'O produto está sem nome.', bloqueiaImportacao: true});

        if (ehPizza && !grupoTamanhoPizza)
          pendencias.push({codigo: 'TAMANHOS_PIZZA_AUSENTES', caminho: caminhoProduto + '.adicionais',
            mensagem: 'Escolha qual grupo contém os tamanhos da pizza.', bloqueiaImportacao: true});

        if (!ehPizza && produto.preco == null)
          pendencias.push({codigo: 'PRECO_PRODUTO_AUSENTE', caminho: caminhoProduto + '.preco',
            mensagem: 'Sem preço — informe para importar.', bloqueiaImportacao: true});
        else if (!ehPizza && produto.preco < 0)
          pendencias.push({codigo: 'PRECO_PRODUTO_INVALIDO', caminho: caminhoProduto + '.preco',
            mensagem: 'O preço não pode ser negativo.', bloqueiaImportacao: true});

        if (grupoTamanhoPizza) {
          const precos = (grupoTamanhoPizza.opcoes || []).map((opcao) => opcao.preco)
            .filter((preco) => preco != null && isFinite(preco) && preco >= 0) as number[];

          if (precos.length) produto.preco = Math.min(...precos);
        }

        (produto.adicionais || []).forEach((adicional, indiceAdicional) => {
          const caminhoAdicional = String(`${caminhoProduto}.adicionais[${indiceAdicional}]`);
          const ehTamanhoPizza = ehPizza && adicional.finalidade === 'TAMANHO_PIZZA';

          if (!adicional.nome || !adicional.nome.trim())
            pendencias.push({codigo: 'NOME_ADICIONAL_AUSENTE', caminho: caminhoAdicional + '.nome',
              mensagem: 'Há um grupo de adicionais sem nome.', bloqueiaImportacao: true});

          if (!ehTamanhoPizza && adicional.tipoSelecao !== 'SIMPLES' && adicional.tipoSelecao !== 'MULTIPLA')
            pendencias.push({codigo: 'TIPO_SELECAO_AUSENTE', caminho: caminhoAdicional + '.tipoSelecao',
              mensagem: String(`Defina o tipo de escolha do grupo "${adicional.nome || 'sem nome'}".`),
              bloqueiaImportacao: true});

          if (!(adicional.opcoes || []).length)
            pendencias.push({codigo: 'ADICIONAL_SEM_OPCOES', caminho: caminhoAdicional + '.opcoes',
              mensagem: String(`O grupo "${adicional.nome || 'sem nome'}" não possui opções.`),
              bloqueiaImportacao: true});

          const minimo = adicional.minimo != null ? adicional.minimo : (adicional.tipoSelecao === 'SIMPLES' ? 1 : 0);
          const maximo = adicional.maximo != null ? adicional.maximo : (adicional.tipoSelecao === 'SIMPLES' ? 1 : 10);

          if (!ehTamanhoPizza && (minimo < 0 || maximo < 1 || minimo > maximo))
            pendencias.push({codigo: 'LIMITES_ADICIONAL_INVALIDOS', caminho: caminhoAdicional,
              mensagem: String(`Os limites do grupo "${adicional.nome || 'sem nome'}" são inválidos.`),
              bloqueiaImportacao: true});

          (adicional.opcoes || []).forEach((opcao, indiceOpcao) => {
            const caminhoOpcao = String(`${caminhoAdicional}.opcoes[${indiceOpcao}]`);

            if (!opcao.nome || !opcao.nome.trim())
              pendencias.push({codigo: 'NOME_OPCAO_AUSENTE', caminho: caminhoOpcao + '.nome',
                mensagem: String(`Há opção sem nome no grupo "${adicional.nome || 'sem nome'}".`),
                bloqueiaImportacao: true});

            if (!ehTamanhoPizza && opcao.preco == null)
              opcao.preco = 0;

            if (opcao.preco == null || opcao.preco < 0)
              pendencias.push({codigo: 'PRECO_OPCAO_INVALIDO', caminho: caminhoOpcao + '.preco',
                mensagem: String(`A opção "${opcao.nome || 'sem nome'}" está sem preço válido.`),
                bloqueiaImportacao: true});
          });
        });
      });
    });

    const avisosAtuais = this.avisosOriginais.filter((aviso) => {
      if (aviso.caminho.indexOf('configuracaoPizza') === 0 && !this.temPizzas) return false;
      if (aviso.codigo === 'COBRANCA_PIZZA_PADRAO' && this.templatePizzaExistente) return false;
      if ((aviso.codigo === 'PEDACOS_PIZZA_PADRAO' || aviso.codigo === 'SABORES_PIZZA_PADRAO') &&
          this.templatePizzaExistente && this.tamanhosNovosTemplatePizza.length === 0) return false;
      return true;
    });

    this.rascunho.pendencias = pendencias.concat(avisosAtuais);
    this.reconstruaViewModel();
  }

  private reconstruaViewModel() {
    const referenciaAberta = this.produtoAberto ? this.produtoAberto.draft.referencia : null;
    const pendenciasAtribuidas = new Set<PendenciaCardapioDraft>();

    this.produtoAberto = null;
    this.categorias = (this.rascunho.categorias || []).map((categoria, indice) => {
      const vm: CategoriaRevisaoVM = {
        draft: categoria,
        indice,
        produtos: [],
        totalPendencias: 0,
        totalBloqueantes: 0
      };

      vm.produtos = (categoria.produtos || []).map((produto, indiceProduto) => {
        const pendencias = this.pendenciasDoProduto(indice, indiceProduto);

        pendencias.forEach((pendencia) => pendenciasAtribuidas.add(pendencia));

        const bloqueado = pendencias.some((pendencia) => pendencia.bloqueiaImportacao);

        const produtoVm: ProdutoRevisaoVM = {
          draft: produto,
          categoria: vm,
          pendencias,
          bloqueado,
          editandoPreco: false,
          precoEdicao: ''
        };

        if (referenciaAberta && produto.referencia === referenciaAberta)
          this.produtoAberto = produtoVm;

        return produtoVm;
      });

      vm.totalPendencias = vm.produtos.reduce((soma, produto) => soma + produto.pendencias.length, 0);
      vm.totalBloqueantes = vm.produtos.filter((produto) => produto.bloqueado).length;

      return vm;
    });

    // pendências que não casam com nenhum produto (ex.: ambiguidades gerais da leitura)
    // viram avisos visíveis no topo da lista — senão o contador aponta pro nada
    this.pendenciasGerais = (this.rascunho.pendencias || [])
      .filter((pendencia) => !pendenciasAtribuidas.has(pendencia));
  }

  private pendenciasDoProduto(indiceCategoria: number, indiceProduto: number): PendenciaCardapioDraft[] {
    const prefixo = String(`categorias[${indiceCategoria}].produtos[${indiceProduto}]`);

    return (this.rascunho.pendencias || [])
      .filter((pendencia) => pendencia.caminho === prefixo || pendencia.caminho.indexOf(prefixo + '.') === 0);
  }

  aoEditarCampo() {
    for (const grupo of this.produtoAberto?.draft.adicionais || []) {
      if (grupo.finalidade === 'COMPONENTE_KIT' && !grupo.configComponenteKit)
        grupo.configComponenteKit = new ConfigComponenteKit();
    }
    if (this.revalidacaoTimer) clearTimeout(this.revalidacaoTimer);

    this.revalidacaoTimer = setTimeout(() => this.revalideLocalmente(), 400);
  }

  get totalProdutos(): number {
    return this.categorias.reduce((soma, categoria) => soma + categoria.produtos.length, 0);
  }

  get totalGrupos(): number {
    return this.categorias.reduce((soma, categoria) => soma + categoria.produtos
      .reduce((subtotal, produto) => subtotal + produto.draft.adicionais.length, 0), 0);
  }

  get totalOpcoes(): number {
    return this.categorias.reduce((soma, categoria) => soma + categoria.produtos
      .reduce((subtotal, produto) => subtotal + produto.draft.adicionais
        .reduce((opcoes, adicional) => opcoes + adicional.opcoes.length, 0), 0), 0);
  }

  get totalPendencias(): number {
    return this.rascunho && this.rascunho.pendencias ? this.rascunho.pendencias.length : 0;
  }

  get totalComPendencia(): number {
    return this.categorias.reduce((soma, categoria) =>
      soma + categoria.produtos.filter((produto) => produto.pendencias.length > 0).length, 0);
  }

  get totalProntos(): number {
    return this.totalProdutos - this.totalComPendencia;
  }

  get totalSelecionados(): number {
    return this.categorias.reduce((soma, categoria) =>
      soma + categoria.produtos.filter((produto) => produto.draft.selecionado && !produto.bloqueado).length, 0);
  }

  get categoriasFiltradas(): CategoriaRevisaoVM[] {
    return this.categorias
      .map((categoria) => ({categoria, produtos: categoria.produtos.filter((produto) => this.passaFiltro(produto))}))
      .filter((par) => par.produtos.length > 0)
      .map((par) => Object.assign({}, par.categoria, {produtos: par.produtos}) as CategoriaRevisaoVM);
  }

  private passaFiltro(produto: ProdutoRevisaoVM): boolean {
    if (this.filtro === 'pendencias' && produto.pendencias.length === 0) return false;
    if (this.filtro === 'prontos' && produto.pendencias.length > 0) return false;

    if (this.busca) {
      const busca = this.busca.toLowerCase();
      const nome = (produto.draft.nome || '').toLowerCase();
      const categoria = (produto.categoria.draft.nome || '').toLowerCase();

      if (nome.indexOf(busca) < 0 && categoria.indexOf(busca) < 0) return false;
    }

    return true;
  }

  defineFiltro(filtro: FiltroRevisao) {
    this.filtro = filtro;
  }

  trackPorReferencia(indice: number, item: any): string {
    return item && item.draft ? item.draft.referencia : indice;
  }

  vaParaCategoria(categoria: CategoriaRevisaoVM) {
    const elemento = document.getElementById(String(`ia-categoria-${categoria.draft.referencia}`));

    if (elemento) elemento.scrollIntoView({behavior: 'smooth', block: 'start'});
  }

  vaParaProximaPendencia() {
    for (const categoria of this.categorias) {
      for (const produto of categoria.produtos) {
        if (produto.pendencias.length > 0) {
          this.filtro = 'todos';
          this.abraProduto(produto);

          // o filtro pode ter acabado de mudar: espera o render para achar a linha do produto
          setTimeout(() => {
            const elemento = document.getElementById(String(`ia-produto-${produto.draft.referencia}`));

            // 'start' + scroll-margin-top da tela: o produto para logo abaixo da barra fixa do painel
            if (elemento) elemento.scrollIntoView({behavior: 'smooth', block: 'start'});
          });

          return;
        }
      }
    }

    // sem pendência de produto: as pendências são avisos gerais da leitura
    if (this.pendenciasGerais.length > 0) {
      this.avisosLeituraAbertos = true;

      setTimeout(() => {
        const avisos = document.getElementById('ia-avisos-leitura');

        if (avisos) avisos.scrollIntoView({behavior: 'smooth', block: 'start'});
      });
    }
  }

  alterneAvisosLeitura() {
    this.avisosLeituraAbertos = !this.avisosLeituraAbertos;
  }

  // seleção

  alterneSelecao(produto: ProdutoRevisaoVM) {
    if (produto.bloqueado) return;

    produto.draft.selecionado = !produto.draft.selecionado;
  }

  todosSelecionados(categoria: CategoriaRevisaoVM): boolean {
    const selecionaveis = categoria.produtos.filter((produto) => !produto.bloqueado);
    return selecionaveis.length > 0 && selecionaveis.every((produto) => produto.draft.selecionado);
  }

  alterneCategoria(categoria: CategoriaRevisaoVM) {
    const marcar = !this.todosSelecionados(categoria);

    for (const produto of categoria.produtos)
      if (!produto.bloqueado) produto.draft.selecionado = marcar;
  }

  // preço inline

  editePreco(produto: ProdutoRevisaoVM, evento?: Event) {
    if (evento) evento.stopPropagation();

    produto.editandoPreco = true;
    produto.precoEdicao = produto.draft.preco != null
      ? produto.draft.preco.toFixed(2).replace('.', ',')
      : '';

    setTimeout(() => {
      const campo: any = document.getElementById(String(`ia-preco-${produto.draft.referencia}`));

      if (campo) {
        campo.focus();
        campo.select();
      }
    });
  }

  confirmePreco(produto: ProdutoRevisaoVM) {
    if (!produto.editandoPreco) return;

    produto.editandoPreco = false;
    const texto = (produto.precoEdicao || '').trim().replace(',', '.');

    if (texto === '') return;

    const preco = Number(texto);

    if (!isFinite(preco) || preco < 0) return;

    produto.draft.preco = Math.round(preco * 100) / 100;
    this.aoEditarCampo();
  }

  cancelePreco(produto: ProdutoRevisaoVM) {
    produto.editandoPreco = false;
  }

  formatePreco(valor: number | null): string {
    if (valor == null) return '—';
    return String(`${valor.toFixed(2).replace('.', ',')}`);
  }

  // drawer

  abraProduto(produto: ProdutoRevisaoVM) {
    this.produtoAberto = produto;
    this.grupoAberto = null;
  }

  fecheProduto() {
    this.produtoAberto = null;
    this.grupoAberto = null;
  }

  alterneGrupo(grupo: AdicionalCardapioDraft) {
    this.grupoAberto = this.grupoAberto === grupo ? null : grupo;
  }

  mudeTipoProduto(produto: ProdutoRevisaoVM, tipo: 'NORMAL' | 'PIZZA' | 'KIT') {
    produto.draft.tipoProduto = tipo;
    if (tipo === 'KIT') produto.draft.configKit = {modalidadeQuantidade: 'FECHADA', qtdeMinimaPorSabor: 1, ...produto.draft.configKit};

    if (tipo === 'NORMAL')
      (produto.draft.adicionais || []).forEach((grupo) => grupo.finalidade = null);
    else if (tipo === 'PIZZA' && !(produto.draft.adicionais || []).some((grupo) => grupo.finalidade === 'TAMANHO_PIZZA')) {
      const grupos = produto.draft.adicionais || [];
      const grupoTamanho = grupos.find((grupo) => this.normalizeNome(grupo.nome).indexOf('tamanho') >= 0) ||
        (grupos.length === 1 ? grupos[0] : null);

      if (grupoTamanho) grupoTamanho.finalidade = 'TAMANHO_PIZZA';
    }

    this.sincronizeConfiguracaoPizza();
    this.carregueTemplatePizza();
    this.revalideLocalmente();
  }

  definaGrupoTamanhoPizza(produto: ProdutoRevisaoVM, grupo: AdicionalCardapioDraft) {
    produto.draft.tipoProduto = 'PIZZA';
    (produto.draft.adicionais || []).forEach((item) =>
      item.finalidade = item === grupo ? 'TAMANHO_PIZZA' : null);
    this.sincronizeConfiguracaoPizza();
    this.carregueTemplatePizza();
    this.revalideLocalmente();
  }

  ehGrupoTamanhoPizza(produto: ProdutoRevisaoVM, grupo: AdicionalCardapioDraft): boolean {
    return produto.draft.tipoProduto === 'PIZZA' && grupo.finalidade === 'TAMANHO_PIZZA';
  }

  renomeieTamanhoPizza(tamanho: TamanhoPizzaImportacaoDraft, novoNome: string) {
    const nomeAnterior = tamanho.nome;
    tamanho.nome = novoNome;

    this.produtosPizza.forEach((produto) => (produto.adicionais || [])
      .filter((grupo) => grupo.finalidade === 'TAMANHO_PIZZA')
      .forEach((grupo) => (grupo.opcoes || []).forEach((opcao) => {
        if (this.normalizeNome(opcao.nome) === this.normalizeNome(nomeAnterior)) opcao.nome = novoNome;
      })));

    this.revalideLocalmente();
  }

  aoEditarConfiguracaoPizza() {
    this.revalideLocalmente();
  }

  mudeCategoriaDoProduto(produto: ProdutoRevisaoVM, referenciaCategoria: string) {
    const origem = produto.categoria;
    const destino = this.categorias.find((categoria) => categoria.draft.referencia === referenciaCategoria);

    if (!destino || destino === origem) return;

    const indiceDraft = origem.draft.produtos.indexOf(produto.draft);

    if (indiceDraft >= 0) origem.draft.produtos.splice(indiceDraft, 1);

    destino.draft.produtos.push(produto.draft);
    this.revalideLocalmente();
  }

  removaProduto(produto: ProdutoRevisaoVM) {
    const categoria = produto.categoria;
    const indiceDraft = categoria.draft.produtos.indexOf(produto.draft);

    if (indiceDraft >= 0) categoria.draft.produtos.splice(indiceDraft, 1);

    if (this.produtoAberto === produto) this.produtoAberto = null;

    this.revalideLocalmente();
  }

  descricaoDoGrupo(grupo: AdicionalCardapioDraft): string {
    if (grupo.finalidade === 'TAMANHO_PIZZA')
      return String(`${grupo.opcoes.length} ${grupo.opcoes.length === 1 ? 'tamanho' : 'tamanhos'} da pizza`);

    const tipo = grupo.tipoSelecao === 'SIMPLES' ? 'escolha 1' :
      (grupo.maximo != null ? String(`até ${grupo.maximo}`) : 'múltipla');

    return String(`${tipo} · ${grupo.opcoes.length} ${grupo.opcoes.length === 1 ? 'opção' : 'opções'}`);
  }

  evidenciaPrincipal(produto: ProdutoRevisaoVM) {
    return produto.draft.evidencias && produto.draft.evidencias.length ? produto.draft.evidencias[0] : null;
  }

  rotuloEvidencia(estado: string): string {
    switch (estado) {
      case 'EXTRAIDO': return 'Lido do cardápio';
      case 'INFERIDO': return 'Deduzido — confira';
      case 'CONFLITANTE': return 'Valores diferentes no cardápio — confira';
      case 'AUSENTE': return 'Não estava no cardápio — confira';
      default: return estado;
    }
  }

  // ── importação ────────────────────────────────────────────────

  get podeImportar(): boolean {
    return !this.importando && this.totalSelecionados > 0;
  }

  abraConfirmacaoImportar() {
    if (this.importando) return;
    if (!this.podeImportar) {
      const primeira = document.querySelector('.ia-check-todos') as HTMLElement;
      return this.foco.mostre(primeira, 'Marque os produtos que quer importar. "todos" marca a categoria inteira.');
    }

    this.confirmandoImportacao = true;
  }

  fecheConfirmacaoImportar() {
    this.confirmandoImportacao = false;
  }

  importeSelecionados() {
    if (!this.podeImportar) return;

    this.sincronizeConfiguracaoPizza();
    this.revalideLocalmente();

    if (!this.podeImportar) return;

    this.confirmandoImportacao = false;

    const referencias: string[] = [];
    const ignorados: string[] = [];

    for (const categoria of this.categorias) {
      for (const produto of categoria.produtos) {
        if (produto.draft.selecionado && !produto.bloqueado) {
          // quem já foi importado numa rodada anterior não vai de novo (evita duplicar no catálogo)
          if (!this.referenciasJaImportadas[produto.draft.referencia])
            referencias.push(produto.draft.referencia);
        } else
          ignorados.push(produto.draft.referencia);
      }
    }

    this.ignorados = ignorados;

    if (referencias.length === 0 && this.resultadoImportacao) {
      this.etapa = 'concluir';
      return;
    }

    this.importando = true;
    this.erroImportacao = null;
    this.etapa = 'concluir';

    this.servico.importe(this.rascunho, referencias, this.idEmpresaAlvo)
      .then((resultado) => {
        this.importando = false;
        this.registreImportados(resultado.importados);
        this.resultadoImportacao = this.mescleResultado(resultado);
      })
      .catch((erro) => {
        this.importando = false;
        this.etapa = 'revisar';
        this.erroImportacao = typeof erro === 'string' ? erro : 'Não foi possível importar. Tente novamente.';
      });
  }

  private registreImportados(referencias: string[]) {
    for (const referencia of referencias)
      this.referenciasJaImportadas[referencia] = true;
  }

  private mescleResultado(resultado: ResultadoImportacaoCardapioIA): ResultadoImportacaoCardapioIA {
    const anteriores = this.resultadoImportacao ? this.resultadoImportacao.importados : [];
    const importados = anteriores
      .filter((referencia) => resultado.importados.indexOf(referencia) < 0)
      .concat(resultado.importados);
    const categoriasAnteriores = this.resultadoImportacao ? this.resultadoImportacao.categoriasCriadas : 0;

    return {
      importados,
      falhas: resultado.falhas,
      categoriasCriadas: categoriasAnteriores + resultado.categoriasCriadas
    };
  }

  // ── etapa 4: conclusão ────────────────────────────────────────

  get resultado(): ResultadoImportacaoCardapioIA {
    return this.resultadoImportacao || {importados: [], falhas: [], categoriasCriadas: 0};
  }

  nomeDaReferencia(referencia: string): string {
    for (const categoria of this.categorias)
      for (const produto of categoria.produtos)
        if (produto.draft.referencia === referencia) return produto.draft.nome || referencia;

    return referencia;
  }

  tenteNovamenteFalhas() {
    const referencias = this.resultado.falhas.map((falha) => falha.referencia);

    if (!referencias.length || this.reimportando) return;

    this.reimportando = true;

    this.servico.importe(this.rascunho, referencias, this.idEmpresaAlvo)
      .then((resultado) => {
        this.reimportando = false;
        this.registreImportados(resultado.importados);
        this.resultadoImportacao = this.mescleResultado(resultado);
      })
      .catch((erro) => {
        this.reimportando = false;
        this.erroImportacao = typeof erro === 'string' ? erro : 'Não foi possível repetir a importação.';
      });
  }

  revejaProduto(referencia: string) {
    this.etapa = 'revisar';

    for (const categoria of this.categorias) {
      for (const produto of categoria.produtos) {
        if (produto.draft.referencia === referencia) {
          this.abraProduto(produto);
          return;
        }
      }
    }
  }
}
